import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Sidebar } from './Sidebar';
import { Navbar } from './Navbar';
import { titleFor } from './navigation';
import { useAuth } from '../../features/auth/useAuth';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { DESKTOP_QUERY, WIDE_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';

const COLLAPSED_STORAGE_KEY = 'shell.sidebar.collapsed';

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    // Navegador con almacenamiento bloqueado: se sigue con el valor por defecto.
    return false;
  }
}

/**
 * Shell persistente del panel administrativo: sidebar lateral y navbar
 * superior, con el contenido de la ruta dentro del `Outlet`.
 *
 * Se monta una sola vez para toda la sesion. Navegar entre secciones cambia
 * unicamente el cuerpo, de modo que el scroll del sidebar, los grupos abiertos
 * y el foco no se reinician en cada clic.
 *
 * El comportamiento responsive se resuelve aqui y no pantalla a pantalla:
 * por encima de 768 px el sidebar es una columna que puede plegarse hasta
 * dejar solo el riel de aplicaciones, y por debajo es un panel deslizante con
 * atrapado de foco y cierre con `Escape`. Ver `docs/UI_STANDARDS.md`.
 */
export function AppShell(): ReactNode {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const isWide = useMediaQuery(WIDE_QUERY);

  const [prefersCollapsed, setPrefersCollapsed] = useState<boolean>(readCollapsedPreference);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Entre 768 y 1279 px el sidebar expandido se come el ancho util de las
  // tablas, asi que ahi se queda en el riel sin tocar la preferencia guardada.
  const isCollapsed = isDesktop && (!isWide || prefersCollapsed);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  useFocusTrap(drawerRef, isDrawerOpen, closeDrawer);

  useEffect(() => {
    if (isDesktop) {
      setIsDrawerOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    document.title = `${titleFor(location.pathname)} · Inventario y Logistica`;
  }, [location.pathname]);

  // Con el panel abierto la pagina de detras no debe desplazarse.
  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [isDrawerOpen]);

  if (!user) {
    return null;
  }

  const setCollapsed = (next: boolean): void => {
    setPrefersCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
    } catch {
      // Preferencia no persistida: la sesion actual sigue funcionando igual.
    }
  };

  const toggleMenu = (): void => {
    if (isDesktop) {
      setCollapsed(!prefersCollapsed);
      return;
    }

    setIsDrawerOpen((open) => !open);
  };

  return (
    <div className="min-h-dvh bg-background">
      <aside
        className={[
          'fixed inset-y-0 left-0 z-30 hidden md:block',
          'transition-[width] duration-200',
          isCollapsed ? 'w-[56px]' : 'w-[264px]',
        ].join(' ')}
      >
        <Sidebar
          role={user.role}
          isCollapsed={isCollapsed}
          onCollapse={() => {
            setCollapsed(true);
          }}
        />
      </aside>

      {isDrawerOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Cerrar la navegacion"
            onClick={closeDrawer}
            className="absolute inset-0 h-full w-full bg-content/50"
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navegacion principal"
            className="absolute inset-y-0 left-0 w-[264px] max-w-[85vw] shadow-xl"
          >
            <button
              type="button"
              onClick={closeDrawer}
              aria-label="Cerrar la navegacion"
              className="absolute right-2 top-3 z-10 grid h-11 w-11 place-items-center rounded-card text-white/80 hover:bg-white/10"
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
            </button>
            <Sidebar
              role={user.role}
              isCollapsed={false}
              onNavigate={closeDrawer}
              onCollapse={closeDrawer}
            />
          </div>
        </div>
      ) : null}

      <div
        className={[
          'flex min-h-dvh flex-col transition-[padding] duration-200',
          isCollapsed ? 'md:pl-[56px]' : 'md:pl-[264px]',
        ].join(' ')}
      >
        <Navbar
          user={user}
          onToggleMenu={toggleMenu}
          onLogout={() => {
            void logout();
          }}
        />

        <main className="w-full flex-1 px-4 py-6 sm:px-6 sm:py-8">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
